import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import {
  ErrorOutline as ErrorOutlineIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Stars as StarsIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@material-ui/icons';

import useDuxiaInsights from './useDuxiaInsights';
import DuxiaInsightCard from './DuxiaInsightCard';
import StoreContext from '../../Store/context';
import './DuxiaInsightsPanel.css';

const DuxiaInsightsPanel = ({ insightsHook: externalHook }) => {
  const history = useHistory();
  const { openChatbotWithPrompt } = useContext(StoreContext) || {};
  const [modalOpen, setModalOpen] = useState(false);

  const localHook = useDuxiaInsights();
  const {
    insightsData,
    loading,
    loadInsights,
    handleDismiss,
    eligibleInsights,
    hasInsights,
  } = externalHook || localHook;

  const handleCtaAction = (insight) => {
    if (!insight?.cta) return;
    const { action_type, prompt, specialist = 'general', route } = insight.cta;
    if (action_type === 'NAVIGATE' && route) {
      history.push(route);
    } else if (prompt && openChatbotWithPrompt) {
      openChatbotWithPrompt(prompt, specialist);
    } else if (route) {
      history.push(route);
    }
    setModalOpen(false);
  };

  const renderCard = (insight, isHeadline = false) => (
    <DuxiaInsightCard
      key={insight.id}
      insight={insight}
      isHeadline={isHeadline}
      onDismiss={handleDismiss}
      onCta={handleCtaAction}
    />
  );

  if (!loading && !hasInsights) {
    return null;
  }

  return (
    <Box className="duxia-insights-panel">
      <Box className="duxia-insights-panel__glow" />

      {/* Header */}
      <Box className="duxia-insights-header">
        <Box className="duxia-insights-brand">
          <Box className="duxia-insights-logo-badge">
            <img src="/image/duxia.jpg" alt="duxIA" className="duxia-insights-logo-img" />
          </Box>
          <Box>
            <Typography className="duxia-insights-title">Insights da duxIA</Typography>
            <Typography className="duxia-insights-subtitle">
              Resumo executivo do ciclo
            </Typography>
          </Box>
        </Box>

        <Box className="duxia-insights-actions">
          {eligibleInsights.totalEligible > eligibleInsights.displayed.length && (
            <Button
              size="small"
              className="duxia-insights-btn-all"
              onClick={() => setModalOpen(true)}
            >
              Ver todos ({eligibleInsights.totalEligible})
            </Button>
          )}

          <Tooltip title="Atualizar dados do ciclo">
            <IconButton
              size="small"
              onClick={() => loadInsights(true)}
              disabled={loading}
              style={{ padding: 4 }}
            >
              {loading ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon style={{ fontSize: '1rem', color: '#64748b' }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      {loading && !insightsData ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : eligibleInsights.displayed.length > 0 ? (
        <Box>
          {/* Headline (Manchete) */}
          {eligibleInsights.headline && renderCard(eligibleInsights.headline, true)}

          {/* Secondary Items (Max 1 if headline exists, or 2 if no headline) */}
          {eligibleInsights.displayed
            .filter((ins) => ins.id !== eligibleInsights.headline?.id)
            .map((insight) => renderCard(insight, false))}
        </Box>
      ) : (
        /* Empty / All caught up State */
        <Box className="duxia-insights-empty">
          <CheckCircleOutlineIcon className="duxia-insights-empty__icon" />
          <Typography className="duxia-insights-empty__title">
            Tudo em ordem no seu negócio! ✨
          </Typography>
          <Typography className="duxia-insights-empty__sub">
            Nenhum alerta crítico ou pendência de configuração no ciclo atual.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            style={{ borderRadius: 16, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
            onClick={() => {
              if (openChatbotWithPrompt) {
                openChatbotWithPrompt(
                  'Olá duxIA, me faça um diagnóstico geral de como está a operação e as finanças da empresa este mês.',
                  'general',
                );
              }
            }}
          >
            Fazer Pergunta à duxIA
          </Button>
        </Box>
      )}

      {/* "Ver Todos" Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box className="duxia-insights-logo-badge" style={{ width: 24, height: 24 }}>
              <img src="/image/duxia.jpg" alt="duxIA" className="duxia-insights-logo-img" />
            </Box>
            <Typography variant="h6" style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>
              Todos os Insights do Negócio ({eligibleInsights.totalEligible})
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent style={{ padding: '16px 20px', maxHeight: '60vh' }}>
          {eligibleInsights.allActive.map((insight) => renderCard(insight, insight.id === eligibleInsights.headline?.id))}
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px' }}>
          <Button onClick={() => setModalOpen(false)} color="primary" style={{ textTransform: 'none', fontWeight: 600 }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DuxiaInsightsPanel;
